'use client';

import cn from '@sindresorhus/class-names';
import Image from 'next/image';
import {memo} from 'react';

import DownloadIcon from '@web-catalog/assets/icons/download_oulline.svg';

import {mockIconURL, RATING_LABELS} from '@web-catalog/const';

import {AppStatus, AppType} from '@web-catalog/types';

import {useOnImageError} from '@web-catalog/hooks/useOnImageError';

import {useSafeOmicronFlag} from '@web-catalog/store';

import {RatingStars, STARS_COLORS} from '@web-catalog/ui-kit/RatingStar';

import {
  formatFileSize,
  numWord,
  shortDownLoadsFormat,
  shortNumFormat,
} from '@web-catalog/utils';
import {
  getOpacityClass,
  getTextColor,
  isHiddenApp,
} from '@web-catalog/utils/appStateUtils';

import {Icon} from '../Icon';
import {Text} from '../Text';
import styles from './AppInfo.module.scss';

type Props = {
  iconUrl?: string;
  appName?: string;
  ageLegal?: string;
  appRating?: number;
  totalRatings?: number;
  downloads?: number;
  fileSize?: number;
  className?: string;
  appStatus: AppStatus;
  hideRating?: boolean;
  appType?: AppType;
};

const formatRating = (rating: number | undefined) => {
  return rating?.toFixed(1).toString().replace('.', ',');
};

export const AppInfo = memo<Props>(
  ({
    iconUrl,
    appName,
    ageLegal,
    appType,
    appRating,
    totalRatings,
    downloads,
    fileSize,
    className,
    appStatus,
    hideRating,
  }) => {
    const onImageError = useOnImageError();
    const isAppHidden = isHiddenApp(appStatus);

    const downloadsRoundRules = useSafeOmicronFlag(
      appType === AppType.GAMES
        ? 'RULES_FOR_DOWNLOADS_COUNT_GAMES'
        : 'RULES_FOR_DOWNLOADS_COUNT_APPS',
    );

    if (!appName) {
      return null;
    }

    return (
      <div className={cn(className, styles.appInfo)}>
        <div
          className={cn(styles.logo, isAppHidden && styles.reducedBottomMargin)}
        >
          <div className={styles.icon}>
            <Image
              className={cn(styles.image, getOpacityClass(appStatus))}
              src={iconUrl ?? mockIconURL}
              alt={appName}
              fill
              onError={onImageError}
            />
          </div>
          <Text
            className={styles.title}
            as="h2"
            typography="subheader-2"
            color={getTextColor(appStatus)}
          >
            {appName}
          </Text>
        </div>
        {isAppHidden && (
          <Text
            color={getTextColor(appStatus)}
            className={styles.hiddenAppSubtitle}
            typography="content-s"
            as="div"
          >
            Приложение недоступно для скачивания
          </Text>
        )}
        <div className={styles.infoTable}>
          {typeof totalRatings !== 'undefined' && !hideRating && (
            <p className={styles.row}>
              <Text
                typography="content-s"
                color="neutral-500"
                className={styles.ratingTitle}
              >
                Рейтинг
              </Text>
              <span className={styles.rating}>
                <span className="visually-hidden">:&nbsp;</span>
                <span className={styles.ratingWrapper}>
                  <Text typography="content-s" color="neutral-500">
                    {formatRating(appRating)}
                  </Text>
                  <RatingStars
                    rating={appRating}
                    color={STARS_COLORS.SECONDARY_APP_SCREEN}
                  />
                </span>
                <Text typography="content-s" color="neutral-500">
                  <Text className="visually-hidden">&nbsp;</Text>
                  {`${shortNumFormat(totalRatings)} ${numWord(
                    totalRatings,
                    RATING_LABELS,
                  )}`}
                </Text>
              </span>
            </p>
          )}
          {!!downloads && !isAppHidden && (
            <>
              <br className="visually-hidden" />
              <p className={cn(styles.row, styles.rowDirection)}>
                <Text typography="content-s" color="neutral-500">
                  Скачиваний
                </Text>
                <Text className="visually-hidden">:&nbsp;</Text>
                <span className={styles.downloads}>
                  <Icon svg={DownloadIcon} />
                  <Text typography="content-s" color="neutral-500">
                    {shortDownLoadsFormat(downloads, downloadsRoundRules)}
                  </Text>
                </span>
              </p>
            </>
          )}
          {!!fileSize && !isAppHidden && (
            <>
              <br className="visually-hidden" />
              <p className={cn(styles.row, styles.rowDirection)}>
                <Text typography="content-s" color="neutral-500">
                  Размер
                </Text>
                <Text className="visually-hidden">:&nbsp;</Text>
                <Text typography="content-s" color="neutral-500">
                  {formatFileSize(fileSize)}
                </Text>
              </p>
            </>
          )}
          {!!ageLegal && !isAppHidden && (
            <>
              <br className="visually-hidden" />
              <p className={cn(styles.row, styles.rowDirection)}>
                <Text typography="content-s" color="neutral-500">
                  Возраст
                </Text>
                <Text className="visually-hidden">:&nbsp;</Text>
                <Text typography="content-s" color="neutral-500">
                  {ageLegal}
                </Text>
              </p>
            </>
          )}
        </div>
      </div>
    );
  },
);

AppInfo.displayName = 'AppInfo';
